import { useCallback, useEffect, useRef, useState } from "react";
import { HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { authFetch } from "@/api/client";
import { SESSION_STORAGE_KEY } from "@/lib/constants";
import { isTokenDue } from "@dolfin/lib";
import { sessionRefresher } from "@/auth/session";

export type PaymentAccountStatus =
  | "NotStarted"
  | "OnboardingInProgress"
  | "Active"
  | "Restricted"
  | "Rejected";

export interface PaymentAccountCapabilities {
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  currentlyDue: string[];
  pastDue: string[];
  disabledReason: string | null;
}

export interface PaymentAccountSnapshot {
  organisationId: string;
  provider: "Stripe" | null;
  accountId: string | null;
  status: PaymentAccountStatus;
  capabilities: PaymentAccountCapabilities | null;
  enabledAt: string | null;
}

// Direct to the backend: WebSockets can't traverse /api/proxy. No fallback: build.ts requires API_BASE_URL,
// and a dev default would point the hub at another environment.
const HUB_BASE = (process.env.API_BASE_URL ?? "") + "/hubs/organisations";

function readSession(): { token: string; activeOrganisationId: string } | null {
  const raw = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.token) return null;
    return { token: parsed.token, activeOrganisationId: parsed.activeOrganisationId };
  } catch {
    return null;
  }
}

interface HookState {
  data: PaymentAccountSnapshot | null;
  isLoading: boolean;
  error: string | null;
  forbidden: boolean;
}

export function usePaymentAccountStatus(organisationId: string | null) {
  const [state, setState] = useState<HookState>({
    data: null,
    isLoading: true,
    error: null,
    forbidden: false,
  });
  const latestSnapshotRef = useRef<PaymentAccountSnapshot | null>(null);

  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      if (!organisationId) return;
      try {
        const response = await authFetch(
          `/api/proxy/v1/organisations/${organisationId}/payment-account/status`,
          { signal },
        );
        if (response.status === 403) {
          setState({ data: null, isLoading: false, error: null, forbidden: true });
          return;
        }
        if (!response.ok) {
          setState((prev) => ({ ...prev, isLoading: false, error: `Status ${response.status}` }));
          return;
        }
        const snapshot = (await response.json()) as PaymentAccountSnapshot;
        latestSnapshotRef.current = snapshot;
        setState({ data: snapshot, isLoading: false, error: null, forbidden: false });
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: err instanceof Error ? err.message : "Failed to load status",
        }));
      }
    },
    [organisationId],
  );

  useEffect(() => {
    setState({ data: null, isLoading: true, error: null, forbidden: false });
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  useEffect(() => {
    if (!organisationId) return;
    const session = readSession();
    if (!session || session.activeOrganisationId !== organisationId) return;

    const hubUrl = `${HUB_BASE}?organisationId=${encodeURIComponent(organisationId)}`;
    const connection = new HubConnectionBuilder()
      // withCredentials must be false: the API's Public CORS omits Allow-Credentials, so the browser blocks negotiate.
      .withUrl(hubUrl, {
        // Called on every (re)connect: renew first if the stored token is about to expire.
        accessTokenFactory: async () => {
          const current = readSession()?.token ?? null;
          if (current && !isTokenDue(current)) return current;
          const outcome = await sessionRefresher.refresh(current);
          return outcome.kind === "refreshed" ? outcome.token : current ?? "";
        },
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    interface StatusPush {
      status: PaymentAccountStatus;
      chargesEnabled: boolean;
      payoutsEnabled: boolean;
      detailsSubmitted: boolean;
      currentlyDue: string[];
      pastDue: string[];
      disabledReason: string | null;
    }

    const handler = (msg: StatusPush) => {
      const prior = latestSnapshotRef.current;
      const next: PaymentAccountSnapshot = {
        organisationId,
        provider: prior?.provider ?? "Stripe",
        accountId: prior?.accountId ?? null,
        status: msg.status,
        capabilities: {
          chargesEnabled: msg.chargesEnabled,
          payoutsEnabled: msg.payoutsEnabled,
          detailsSubmitted: msg.detailsSubmitted,
          currentlyDue: msg.currentlyDue,
          pastDue: msg.pastDue,
          disabledReason: msg.disabledReason,
        },
        enabledAt:
          msg.status === "Active" && !prior?.enabledAt
            ? new Date().toISOString()
            : (prior?.enabledAt ?? null),
      };
      latestSnapshotRef.current = next;
      setState({ data: next, isLoading: false, error: null, forbidden: false });
    };

    connection.on("paymentAccountStatusChanged", handler);
    connection.start().catch((err) => console.error("payment SignalR connect failed", err));

    return () => {
      connection.off("paymentAccountStatusChanged", handler);
      if (connection.state !== HubConnectionState.Disconnected) {
        connection.stop().catch(() => {});
      }
    };
  }, [organisationId]);

  return { ...state, refresh };
}
